import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc, 
  doc, 
  orderBy, 
  limit, 
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  User, 
  UserRole, 
  Course, 
  Lesson, 
  StudentEnrollment, 
  VideoProgress, 
  StudentProgress, 
  TeacherDashboardStats
} from '../types';
import { getUserStats } from './userService';

// Interfaz para representar a un estudiante en un curso
interface StudentInCourse {
  userId: string;
  name?: string;
  progress: number;
}

// Extender la interfaz TeacherDashboardStats para incluir studentsInCourses
export interface AdminDashboardStatsExtended extends TeacherDashboardStats {
  studentsInCourses: { [courseId: string]: StudentInCourse[] };
  activeCourses: number;
  weeklyActivity: Array<{ day: string; count: number }>;
  maxDailyActivity: number;
  dailyEngagementRate: number;
  averageSessionTime: number;
  courseCompletionRate: number;
  activeStudentsPercentage: number;
  completedQuizzes: number;
  totalStudents: number;
  totalCourses: number;
  totalLessons: number;
  totalRevenue: number;
  averageProgress: number;
  topPerformingStudents: StudentProgress[];
  lowEngagementStudents: StudentProgress[];
  recentEnrollments: StudentEnrollment[];
  activeStudents: number;
  totalWatchTime: number;
  averageEngagement: number;
  studentsPerCourse: { [courseId: string]: number };
  progressPerCourse: { [courseId: string]: number };
  teachers: {
    total: number;
    verified: number;
    pending: number;
  };
  students: {
    total: number;
    verified: number;
    pending: number;
  };
  total: number;
}

// الحصول على إحصائيات لوحة تحكم الأدمن
export const getAdminDashboardStats = async (): Promise<AdminDashboardStatsExtended | null> => {
  try {
    // جلب إحصائيات المستخدمين أولاً
    const userStatsResult = await getUserStats();
    
    // جلب جميع الكورسات
    const coursesQuery = query(collection(db, 'courses'));
    const coursesSnapshot = await getDocs(coursesQuery);
    const courses: Course[] = [];
    
    coursesSnapshot.forEach(doc => {
      const courseData = doc.data() as Course;
      courses.push({
        ...courseData,
        id: doc.id
      });
    });
    
    // جلب تسجيلات الطلاب
    const enrollmentsQuery = query(collection(db, 'enrollments'), orderBy('createdAt', 'desc'), limit(10));
    const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
    const recentEnrollments: StudentEnrollment[] = [];
    const studentIds: string[] = [];
    const studentsPerCourse: { [courseId: string]: number } = {};
    const studentsInCourses: { [courseId: string]: StudentInCourse[] } = {};
    
    // معالجة بيانات التسجيلات
    for (const docSnapshot of enrollmentsSnapshot.docs) {
      const enrollmentData = docSnapshot.data();
      const enrollment = { 
        ...enrollmentData, 
        id: docSnapshot.id 
      } as StudentEnrollment;
      
      // إضافة إلى قائمة التسجيلات الحديثة
      recentEnrollments.push(enrollment);
      
      // تتبع معرفات الطلاب الفريدة
      if (!studentIds.includes(enrollment.userId)) {
        studentIds.push(enrollment.userId);
      }
      
      // حساب عدد الطلاب لكل كورس
      if (!studentsPerCourse[enrollment.courseId]) {
        studentsPerCourse[enrollment.courseId] = 0;
        studentsInCourses[enrollment.courseId] = [];
      }
      studentsPerCourse[enrollment.courseId]++;
      
      // إضافة معلومات الطالب إلى قائمة الطلاب في الكورس
      // Usamos la propiedad progress o completionPercentage según esté disponible
      const studentProgress: number = 
        'progress' in enrollmentData ? enrollmentData.progress : 
        'completionPercentage' in enrollmentData ? enrollmentData.completionPercentage : 0;
      
      studentsInCourses[enrollment.courseId].push({
        userId: enrollment.userId,
        name: 'studentName' in enrollmentData ? enrollmentData.studentName : undefined,
        progress: studentProgress
      });
    }
    
    // جلب معلومات المستخدمين
    const userPromises = studentIds.map(async (userId) => {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return { id: userId, ...userDoc.data() } as User;
      }
      return null;
    });
    
    const users = (await Promise.all(userPromises)).filter(user => user !== null) as User[];
    
    // إضافة أسماء المستخدمين إلى قائمة الطلاب في الكورسات
    for (const courseId in studentsInCourses) {
      studentsInCourses[courseId] = studentsInCourses[courseId].map(student => {
        const user = users.find(u => u.id === student.userId);
        return {
          ...student,
          name: user ? user.name : 'طالب'
        };
      });
    }
    
    // جلب بيانات تقدم الطلاب
    const studentProgressData = await calculateStudentProgress(recentEnrollments);
    
    // حساب إجمالي الإيرادات (قيمة افتراضية للتوضيح)
    const totalRevenue = courses.reduce((sum, course) => {
      const studentsCount = studentsPerCourse[course.id] || 0;
      return sum + (course.price * studentsCount);
    }, 0);
    
    // إعداد البيانات النهائية
    const result: AdminDashboardStatsExtended = {
      totalStudents: studentIds.length,
      totalCourses: courses.length,
      totalLessons: courses.reduce((sum, course) => sum + (course.lessonsCount || 0), 0),
      totalRevenue,
      averageProgress: studentProgressData.averageProgress,
      topPerformingStudents: studentProgressData.topPerformingStudents,
      lowEngagementStudents: studentProgressData.lowEngagementStudents,
      recentEnrollments,
      activeStudents: studentIds.length, // تقدير للطلاب النشطين
      totalWatchTime: 0, // قيمة افتراضية
      averageEngagement: 0, // قيمة افتراضية
      studentsPerCourse: studentsPerCourse, // عدد الطلاب لكل كورس
      studentsInCourses: studentsInCourses, // قائمة الطلاب المشتركين في كل كورس
      progressPerCourse: {}, // قيمة افتراضية
      activeCourses: courses.filter(c => c.isPublished === true).length,
      weeklyActivity: generateWeeklyActivity(),
      maxDailyActivity: 100, // قيمة افتراضية أو يمكن حسابها من البيانات الفعلية
      dailyEngagementRate: 0.75, // قيمة افتراضية (75%)
      averageSessionTime: 30 * 60, // قيمة افتراضية (30 دقيقة بالثواني)
      courseCompletionRate: calculateCourseCompletionRate(studentsPerCourse, courses),
      activeStudentsPercentage: studentIds.length > 0 ? (studentIds.length / userStatsResult.totalStudents) * 100 : 0,
      completedQuizzes: 0, // قيمة افتراضية
      teachers: {
        total: userStatsResult.totalTeachers,
        verified: userStatsResult.totalTeachers - (userStatsResult.unverifiedUsers / 2), // تقدير تقريبي
        pending: userStatsResult.unverifiedUsers / 2, // تقدير تقريبي
      },
      students: {
        total: userStatsResult.totalStudents,
        verified: userStatsResult.totalStudents - (userStatsResult.unverifiedUsers / 2), // تقدير تقريبي
        pending: userStatsResult.unverifiedUsers / 2, // تقدير تقريبي
      },
      total: userStatsResult.totalUsers
    };
    
    return result;
  } catch (error) {
    console.error('Error getting admin dashboard stats:', error);
    return null;
  }
};

// حساب تقدم الطلاب وتفاصيل المشاركة
const calculateStudentProgress = async (enrollments: StudentEnrollment[]): Promise<{
  topPerformingStudents: StudentProgress[];
  lowEngagementStudents: StudentProgress[];
  averageProgress: number;
  progressDistribution: { [range: string]: number };
  completionRates: { [courseId: string]: number };
}> => {
  try {
    const allProgress: StudentProgress[] = [];
    let totalProgress = 0;
    const progressDistribution: { [range: string]: number } = {
      '0-25': 0,
      '26-50': 0,
      '51-75': 0,
      '76-100': 0
    };
    const completionRates: { [courseId: string]: number } = {};
    const courseStudentCounts: { [courseId: string]: number } = {};
    const courseCompletedCounts: { [courseId: string]: number } = {};

    // جمع معلومات التقدم لكل تسجيل
    for (const enrollment of enrollments) {
      // الحصول على معلومات المستخدم
      const userDoc = await getDoc(doc(db, 'users', enrollment.userId));
      if (!userDoc.exists()) continue;
      const userData = userDoc.data() as User;

      // الحصول على معلومات الكورس
      const courseDoc = await getDoc(doc(db, 'courses', enrollment.courseId));
      if (!courseDoc.exists()) continue;
      const courseData = courseDoc.data() as Course;

      // الحصول على تقدم الفيديو للطالب
      const videoProgressQuery = query(
        collection(db, 'videoProgress'),
        where('userId', '==', enrollment.userId),
        where('courseId', '==', enrollment.courseId)
      );
      const videoProgressSnapshot = await getDocs(videoProgressQuery);
      const videoProgressList = videoProgressSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        watchedSeconds: doc.data().watchedSeconds || 0,
        lastWatchDate: doc.data().lastWatchDate || 0
      }));

      // حساب وقت المشاهدة الإجمالي
      const totalWatchTime = videoProgressList.reduce((sum, vp) => sum + (vp.watchedSeconds || 0), 0);
      
      // الحصول على آخر تاريخ مشاهدة
      const lastWatchDates = videoProgressList.map(vp => vp.lastWatchDate || 0);
      const lastActivityDate = lastWatchDates.length > 0 ? Math.max(...lastWatchDates) : enrollment.lastAccessDate || 0;

      // حساب عدد الدروس المكتملة
      const completedLessons = enrollment.completedLessons?.length || 0;

      // الحصول على إجمالي عدد الدروس في الكورس
      const lessonsQuery = query(
        collection(db, 'lessons'),
        where('courseId', '==', enrollment.courseId)
      );
      const lessonsSnapshot = await getDocs(lessonsQuery);
      const totalLessons = lessonsSnapshot.size;

      // حساب نسبة التقدم إذا لم تكن موجودة
      const progress = enrollment.progress !== undefined ? enrollment.progress : 
        (totalLessons > 0 ? completedLessons / totalLessons : 0);

      // حساب درجة المشاركة
      const engagementScore = calculateEngagementScore(progress, totalWatchTime, completedLessons, totalLessons);

      // إضافة إلى مصفوفة التقدم
      allProgress.push({
        userId: enrollment.userId,
        userName: userData.name,
        userEmail: userData.email,
        userPhoto: userData.photoURL,
        courseId: enrollment.courseId,
        courseName: courseData.title,
        enrollmentDate: enrollment.enrollmentDate,
        lastAccessDate: enrollment.lastAccessDate,
        progress: progress,
        completedLessons,
        totalLessons,
        watchTime: totalWatchTime,
        engagementScore,
        createdAt: enrollment.createdAt,
        updatedAt: enrollment.updatedAt,
        lastActivityDate: lastActivityDate || enrollment.lastAccessDate || 0
      } as StudentProgress);

      // تحديث إجمالي التقدم
      totalProgress += progress;

      // تحديث توزيع التقدم
      const progressPercent = progress * 100;
      if (progressPercent <= 25) progressDistribution['0-25']++;
      else if (progressPercent <= 50) progressDistribution['26-50']++;
      else if (progressPercent <= 75) progressDistribution['51-75']++;
      else progressDistribution['76-100']++;

      // تحديث معدلات الإكمال للكورسات
      if (!courseStudentCounts[enrollment.courseId]) {
        courseStudentCounts[enrollment.courseId] = 0;
        courseCompletedCounts[enrollment.courseId] = 0;
      }
      courseStudentCounts[enrollment.courseId]++;
      if (progress >= 0.9) { // اعتبار الكورس مكتمل إذا كان التقدم 90% أو أكثر
        courseCompletedCounts[enrollment.courseId]++;
      }
    }

    // حساب معدلات الإكمال للكورسات
    Object.keys(courseStudentCounts).forEach(courseId => {
      completionRates[courseId] = courseStudentCounts[courseId] > 0 ?
        courseCompletedCounts[courseId] / courseStudentCounts[courseId] : 0;
    });

    // حساب متوسط التقدم
    const averageProgress = enrollments.length > 0 ? totalProgress / enrollments.length : 0;

    // ترتيب الطلاب حسب التقدم والمشاركة
    const sortedByProgress = [...allProgress].sort((a, b) => 
      (b.progress || 0) - (a.progress || 0)
    );
    const sortedByEngagement = [...allProgress].sort((a, b) => 
      (b.engagementScore || 0) - (a.engagementScore || 0)
    );

    return {
      topPerformingStudents: sortedByProgress.slice(0, 5),
      lowEngagementStudents: sortedByEngagement.reverse().slice(0, 5),
      averageProgress,
      progressDistribution,
      completionRates
    };
  } catch (error) {
    console.error('Error calculating student progress:', error);
    throw error;
  }
};

// توليد بيانات النشاط الأسبوعي
const generateWeeklyActivity = (): Array<{ day: string; count: number }> => {
  const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
  return days.map(day => ({
    day,
    count: Math.floor(Math.random() * 100) + 10 // قيم عشوائية للتوضيح
  }));
};

// حساب معدل إكمال الدورات
const calculateCourseCompletionRate = (
  studentsPerCourse: { [courseId: string]: number },
  courses: Course[]
): number => {
  if (courses.length === 0) return 0;
  
  // حساب متوسط نسبة الطلاب الذين أكملوا كل دورة
  const totalEnrollments = Object.values(studentsPerCourse).reduce((sum, count) => sum + count, 0);
  if (totalEnrollments === 0) return 0;
  
  // نفترض أن 60% من الطلاب المسجلين أكملوا الدورات (قيمة افتراضية للتوضيح)
  return 60;
};

// حساب درجة المشاركة
export const calculateEngagementScore = (
  progress: number,
  watchTime: number,
  completedLessons: number,
  totalLessons: number
): number => {
  // حساب درجة المشاركة بناءً على التقدم ووقت المشاهدة
  // يمكن تعديل هذه الصيغة حسب احتياجات المنصة
  
  // وزن كل عامل
  const progressWeight = 0.5;
  const watchTimeWeight = 0.3;
  const completionRateWeight = 0.2;
  
  // تطبيع وقت المشاهدة (افتراض أن 3600 ثانية هو الحد الأقصى المتوقع)
  const normalizedWatchTime = Math.min(watchTime / 3600, 1);
  
  // حساب معدل إكمال الدروس
  const completionRate = totalLessons > 0 ? completedLessons / totalLessons : 0;
  
  // حساب الدرجة النهائية (0-10)
  const score = (
    (progress * progressWeight) +
    (normalizedWatchTime * watchTimeWeight) +
    (completionRate * completionRateWeight)
  ) * 10;
  
  return Math.min(Math.max(score, 0), 10);
};
